import React, { useEffect, useMemo, useRef, useState } from "react";
import "./PrimaryReturn.css";
import "../viewOrder/ViewOrder.css";
import "../manageBasket/ManageBasket.css";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import CustomGenericModal from "../../components/dms_common/CustomGenericModal";
import { GenericModal, getGenericModalProps, initialGenericModalProps } from "../viewOrder/ViewOrderUtils";
import { Alert, Snackbar } from "@mui/material";
import HeaderV2 from "../../components/dms_common/HeaderV2";
import dayjs, { Dayjs } from "dayjs";
import useSessionStorageDates from "../../components/dms_common/UseSessionStorageDates";
import GenericSummaryCard, { GenericSummaryCardItemObject } from "../../components/dms_common/GenericSummaryCard";
import TextSwitcher, { TextSwitcherItemObject } from "../../components/dms_common/TextSwitcher";
import { Loader } from "../../components/loader/Loader";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import MultiCategoryFilterV2 from "../../components/dms_common/MultiCategoryFilterV2";
import { PrimaryReturnConfig, PrimaryReturnRows } from "./PrimaryOrderConfig";
import { orderSourceConfig, orderStatusConfig } from "../../types/types";
import { Button } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { isGapMoreThanNDays, isStartDateIsGreaterThanEndDate } from "../../utils/calenderUtils";
import { defaultSearchFunction, filterData, updateDatesInLocalStorage } from "../../components/dms_common/DmsCommonServices";
import { getReturnList, getUTCDate } from "../../services/manageOrderService";
import { sendSlackAlert, SlackChannel } from "../../services/slackService";
import { formatAmount, safeStringify, sortRows, truncateToTwoDecimals } from "../../components/dms_common/DmsUtils";
import CreateNewPrimaryReturn from "./PrimaryReturnComponents/CreateNewPrimaryReturn";
import { useNavigate } from "react-router-dom";
import { clearBatchDetailsInDbPrimary } from "../SalesReturn/SalesReturnService";
import { getConfigData } from "../../services/configurationService";
import { parseInt } from "lodash";

const PrimaryReturn = () => {
  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );
  const currencySymbol = ConfigDictionary?.currency ?? "";
  const {caseUnitLabelPrimaryReturn, packUnitLabelPrimaryReturn, otherUnitLabelPrimaryReturn} = ConfigDictionary;

  const { storedStartDate, storedEndDate, storeSessionStorageDates } =
    useSessionStorageDates("primaryReturn");
  const navigate = useNavigate();
  const [loader, setLoader] = useState<boolean>(true);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
    initialGenericModalProps
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [resetSearch, setResetSearch] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Dayjs>(dayjs());
  const [startDate, setStartDate] = useState<Dayjs>(storedStartDate);
  const [endDate, setEndDate] = useState<Dayjs>(storedEndDate);
  const [summaryItems, setSummaryItems] = useState<
    GenericSummaryCardItemObject[]
  >([]);
  const [textSwitcherItems, setTextSwitcherItems] = useState<
    TextSwitcherItemObject[]
  >([]);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [selectedFilterType, setSelectedFilterType] = useState<string>("status");
  const [selectedStatusTypes, setSelectedStatusTypes] = useState<string[]>([]);
  const [approvalStagesList, setApprovalTrackingList] = useState<
    orderStatusConfig[]
  >([]);
  const orderSourcesRef = useRef<Record<string, orderSourceConfig>>({});

  //NOTE: Need to fetch these from API, and provide interface
  const [rows, setRows] = useState<PrimaryReturnRows[]>([]);
  const [allData, setAllData] = useState<PrimaryReturnRows[]>([]);
  const [sortByColumn, setSortByColumn] = useState<any>();

  const [open, setOpen] = useState(false);

  const [createBtnDisabled, setCreateBtnDisabled] = useState<boolean>(false);
  const validDateRange = ConfigDictionary.dateRangeConfig ?? 31;

  const feature = "primary";
  const pageID = "PrimaryReturn";

  

  const filteredData = useMemo(() => {
    let filtered = filterData(allData, searchedValue.toLowerCase(), defaultSearchFunction);

    if (selectedStatusTypes.length > 0) {
      filtered = filtered.filter(row => selectedStatusTypes.includes(row.status));
    }

    return filtered;
  }, [searchedValue, allData, selectedStatusTypes]);

 
  useEffect(() => {
    setRows(filteredData); 
  }, [filteredData])

  const statusFilterMenuItems = [
     {
      id: "PENDING",
      label: "Requested",
      count: allData.filter((invoice) => invoice.status?.toUpperCase() === "PENDING").length
    },
    {
      id: "APPROVED",
      label: "Approved",
      count: allData.filter((invoice) => invoice.status?.toUpperCase() === "APPROVED").length
    },
    {
      id: "REJECTED",
      label: "Rejected",
      count: allData.filter((invoice) => invoice.status?.toUpperCase() === "REJECTED").length
    },
  ];

  const filters = [
    {
      id: "status",
      label: "Status",
      options: statusFilterMenuItems,
      selectedOptions: selectedStatusTypes,
      setSelectedOptions: setSelectedStatusTypes,
    },
  ];

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };




  function canWeMakeRequest() {
    if (
      startDate &&
      endDate &&
      isGapMoreThanNDays(startDate, endDate, validDateRange)
    ) {

      setGenericModalProps(
        getGenericModalProps(
          "positive",
          `You can't select date range more than ${validDateRange} days!`
        )
      );
      return false;
    }
    if (
      startDate &&
      endDate &&
      isStartDateIsGreaterThanEndDate(startDate, endDate)
    ) {
      setGenericModalProps(
        getGenericModalProps(
          "positive",
          "To Date should be greater than from date."
        )
      );
      return false;
    }
    updateDatesInLocalStorage(startDate, endDate, feature, pageID);
    return true;
  }

  const getReturns = async (startDate: string, endDate: string) => {
    setLastRefreshed(dayjs());
    setResetSearch(true);
    setTimeout(() => setResetSearch(false), 0);

    try {
      setLoader(true);

      const response = await getReturnList(startDate, endDate, "primaryreturn", "primary");
      if (response?.data?.features?.length > 0) {
        let responseArray: PrimaryReturnRows[] = [];
        response?.data?.features?.forEach((item: any, index: any) => {
          const {
            id,
            referenceNumber,
            creationTime,
            grnLastModifiedTime,
            totalCaseQuantity,
            totalPieceQuantity,
            totalOtherUnitQuantity,
            netAmount,
            status,
            invoiceNumber,
            grnNumber,
            normalizedQuantity,
          } = item || {};

          let approvalDate = "-";
          if (status.toUpperCase() === "PENDING"){
            approvalDate = "-";
          } else {
            approvalDate = grnLastModifiedTime;
          }

          responseArray.push({
            id,
            returnInvoiceNumber: invoiceNumber,
            invoiceNumber: referenceNumber,
            grnNumber: grnNumber,
            returnDate: creationTime.split(" ")[0],
            approvalDate: approvalDate,
            returnCaseQuantity: Math.abs(parseInt(totalCaseQuantity)),
            returnOtherUnitQuantity: Math.abs(parseInt(totalOtherUnitQuantity)),
            returnPieceQuantity: Math.abs(parseInt(totalPieceQuantity)),
            returnValue: truncateToTwoDecimals(Math.abs(parseFloat(netAmount))),
            normalizedQuantity: Math.abs(parseInt(normalizedQuantity)),
            status: status,
            showEyeIcon: true,
          });
        });
  

        setRows(responseArray);
        setAllData(responseArray)
        setSearchedValue("");
      } else {
        setRows([]);
      }
    } catch (error) {
      await sendSlackAlert(
        SlackChannel.DMS_PRIMARY_UI_NOTIFICATION,
        "Error Fetching Returns - Primary Returns",
        `Failed to fetch order data for startDate ${startDate} and endDate ${endDate} in ManagePrimaryOrder. Error: ${
          (error as Error).message
        }`,
        safeStringify({
          error,
          startDate,
          endDate,
          file: "PrimaryReturn.tsx",
        })
      );
      setRows([]);
      setGenericModalProps(
        getGenericModalProps("error", "Something went wrong")
      );
    } finally {
      setLoader(false);
    }
  };


  const getInitalTableData = async () => {
    if (canWeMakeRequest()) {
      storeSessionStorageDates(startDate, endDate);
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");
      await getReturns(start, end);
    }
  };

    const fetchRequiredConfigs = async () => {
    try {
      const orderTrackingStages = await getConfigData(
        "dms_configuration",
        "primaryReturnTrackingStages"
      );
      setApprovalTrackingList(orderTrackingStages[0]?.configValue ?? []);

    } catch (e) {
      console.error("Error while fetching configs", e);
      setGenericModalProps(
        getGenericModalProps(
          "error",
          "Error while fetching configs"
        )
      )
    }
  };

  const resetGenericModalProps = () => {
    setGenericModalProps(initialGenericModalProps);
  }
 
  const init = async () => {
    await fetchRequiredConfigs();
    await getInitalTableData();
  };
  useEffect(() => {
    const interval = setInterval(() => {
      getInitalTableData();
      resetGenericModalProps()
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [startDate, endDate]);

  useEffect(() => {
    init();
  }, [startDate, endDate]);


  useEffect(() => {
    if (sortByColumn?.key && sortByColumn.direction !== "none") {
      const { key, direction, type } = sortByColumn;
      const sorted = sortRows(rows, key, direction, type);
      setRows(sorted);
    }
  }, [sortByColumn]);

  const calculateOrderCount = () => {
    let totalReturnedCaseQuantity = 0
    let totalReturnedPieceQuantity = 0
    let totalReturnedOtherQuantity = 0
    let totalReturnValue = 0

    rows.forEach((item) => {
      totalReturnedCaseQuantity += item.returnCaseQuantity
      totalReturnedPieceQuantity += item.returnPieceQuantity
      totalReturnedOtherQuantity += item.returnOtherUnitQuantity
      totalReturnValue += parseFloat(item.returnValue)
    });

    const qtyDisplayParts: string[] = [];
    if (caseUnitLabelPrimaryReturn) {
      qtyDisplayParts.push(`${totalReturnedCaseQuantity} ${caseUnitLabelPrimaryReturn}`);
    }

    if (otherUnitLabelPrimaryReturn) {
      qtyDisplayParts.push(`${totalReturnedOtherQuantity} ${otherUnitLabelPrimaryReturn}`);
    }

    if (packUnitLabelPrimaryReturn) {
      qtyDisplayParts.push(`${totalReturnedPieceQuantity} ${packUnitLabelPrimaryReturn}`);
    }


    const qtyDisplay = qtyDisplayParts.join(", ");
    setSummaryItems([
      {
        itemLabel: "Total Return Quantity",
        itemValue2: `${qtyDisplay}`,
        svgIcon: "pendingOrdersSVG",
        itemStyles: {
          itemValue1: {
            fontWeight: "bold",
          },
          itemValue2: {
            color: "#CC8034",
            backgroundColor: "#FFFBE7",
            fontSize: "1.2em"
          },
          svgIcon: {
            color: "#CC8034"
          }
        },
      },
      {
        itemLabel: "Total Return Value",
        itemValue2: formatAmount(totalReturnValue, currencySymbol),
        svgIcon: "invoicedOrdersSVG",
        itemStyles: {
          itemValue1: {
            fontWeight: "bold",
          },
          itemValue2: {
            color: "#5A865A",
            backgroundColor: "#EAFFEA",
            fontSize: "1.2em"
          },
          svgIcon: {
            color: "#5A865A",
          },
        },
      },
    ]);
  };

  useEffect(() => {
    calculateOrderCount();
  }, [rows]);

  const handleCreateBtnClick = () => {
    setOpen(true);
  }

  const handleRedirect = (url: string, row: PrimaryReturnRows) => {
    if (!row.invoiceNumber){
      navigate("viewWithoutGRN", {
        state: {
          invoiceNumber: row.returnInvoiceNumber,
        }
      });
    } else {
      navigate("view" , {
        state: {
          invoiceNumber: row.returnInvoiceNumber,
          referenceNumber: row.invoiceNumber,
        }
      })
    }
  }
  const primaryReturnApproval = ConfigDictionary.primaryReturnApproval ?? true;
 
  return (
    <div className="primary-return-parent">
      <ScreenOverlay apply={loader} />

      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        modalOpen={genericModalProps?.open}
        onClose={() => setGenericModalProps(initialGenericModalProps)}
      />

      <CreateNewPrimaryReturn
        open={open}
        onClose={() => setOpen(false)}
        title="Create New Return"
      />

      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>

      <div 
        style={{
          display: "flex",
          gap: "6px",
          alignItems: "center"
        }}
      >
        <HeaderV2
          title={"Primary Return"}
          searchFunction={loader ? () => {} : handleSearchValueChange}
          onRefresh={init}
          resetSearch={resetSearch}
          lastRefreshed={lastRefreshed}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
        />

        <button
          className="create-return-btn"
          onClick={handleCreateBtnClick}
          disabled={createBtnDisabled}

          >
            <div style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
              <AddCircleIcon sx={{ fontSize: "13px", marginRight: "5px" }} />{" "}
              <span style={{whiteSpace: "nowrap", fontWeight: 500}}>Create Return</span>
            </div>
          </button>
        
      </div>
      <div style={{ fontSize: "0.75rem", display: "flex", justifyContent: 'space-between', alignItems: "flex-end" }}>
        <GenericSummaryCard
          cardTitle="Summary"
          itemObjects={summaryItems}
        />
      

      { primaryReturnApproval && 
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "9px",
            }}
          >
            <TextSwitcher
              textSwitcherItems={textSwitcherItems ?? []}
              interval={3000}
            />
              <MultiCategoryFilterV2
                isOpen={filterOpen}
                setIsOpen={setFilterOpen}
                activeFilter={selectedFilterType}
                setActiveFilter={setSelectedFilterType}
                filters={filters}
                label="More Filters"
                searchValue={searchedValue}
                onSearchChange={setSearchedValue}
                showSearch={true}
                showMoreFilters={false}
              />
          </div>
        }
      </div>
      {loader ? (
        <Loader sx={{ position: "absolute", top: "55%" }} />
      ) : (
        <CustomDataGrid
          rows={Array.isArray(rows) ? rows : []}
          columnsMetaData={PrimaryReturnConfig()}
          orderStatusConfig={approvalStagesList}
          redirectUrl="/view"
          redirectUrlFunction={handleRedirect}
          tableWidth={0.77}
          orderSourceConfig={orderSourcesRef.current}
          enableRowClickRedirect={true}
          isFooterVisible={true}
          insideManageOrder={true}
          isPrimaryOrder={true}
          setSortByColumn={setSortByColumn}
        />
      )}
    </div>

  );
}

export default PrimaryReturn;
